import React, { useContext, type JSX } from 'react';
import { Query } from '@apollo/client/react/components';
import query from './EkonomiskAnalysBaseParamsQuery.gql';
import { KodplanConsumer } from '../../../../../../../Components/KodplanManager/Context/KodplanContext';
import {
  EkonomiskAnalysBaseParamsQuery as QueryData,
  EkonomiskAnalysBaseParamsQueryVariables,
  EkonomiskAnalysBaseParamsQuery_ekanalysstandardcolumns,
} from './_QueryTypes/EkonomiskAnalysBaseParamsQuery';
import { ApolloError } from '@apollo/client';
import { ISumCategory } from '../EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { IGrundurval } from '../../EkonomiskAnalysPage';
import { notNullOrUndefined } from '../../../../../../../Utils/utils';

export type StandardColumnType =
  EkonomiskAnalysBaseParamsQuery_ekanalysstandardcolumns;

interface IChildrenProps {
  availableCategories?: ISumCategory[];
  error?: ApolloError | Error;
  grundUrval?: IGrundurval[];
  hasWholeCodeBook?: boolean;
  loading: boolean;
  standardColumns?: StandardColumnType[];
}

interface IProps {
  children: (childrenProps: IChildrenProps) => JSX.Element | null;
}

const STANDARD_GRUNDURVAL_VISIBILITY = '5';

const EkonomiskAnalysBaseParamsQuery: React.FunctionComponent<
  IProps
> = props => {
  const { activeDatabase } = useContext(ActiveDatabaseContext);

  return (
    <KodplanConsumer>
      {({ activeKodplan }) => {
        const codeplanId = activeKodplan ? activeKodplan.id : undefined;
        return (
          <Query<QueryData, EkonomiskAnalysBaseParamsQueryVariables>
            query={query}
            variables={{
              codeplanId: codeplanId || '',
              dbName: activeDatabase ? activeDatabase : '',
            }}
            skip={!codeplanId}
          >
            {({ loading, data, error }) => {
              if (loading || error) {
                return props.children({ loading, error });
              }
              if (!data) {
                return props.children({
                  loading: false,
                  error: new Error('Could not get base parameters'),
                });
              }
              const availableCategories =
                data && data.ekanalysstandardsumcategories
                  ? data.ekanalysstandardsumcategories
                      .filter(notNullOrUndefined)
                      .map(x => {
                        return {
                          name: x.name || 'NAMN SAKNAS',
                          code: x.code,
                          subCategories: x.categories
                            ? x.categories
                                .filter(notNullOrUndefined)
                                .map(category => {
                                  return {
                                    name: category.name,
                                    code: category.code,
                                    sums: category.sums
                                      ? category.sums
                                          .filter(notNullOrUndefined)
                                          .map(sum => {
                                            return {
                                              LOPNR: sum.LOPNR,
                                              Namn: sum.Namn,
                                              ORDNINGS_NR_KONTODEL:
                                                sum.ORDNINGS_NR_KONTODEL,
                                              STARTPOS: sum.STARTPOS,
                                              SLUTPOS: sum.SLUTPOS,
                                              GRUPPERINGSTYP:
                                                sum.GRUPPERINGSTYP,
                                              DOLJ_NIVA: sum.DOLJ_NIVA,
                                              SIDBRYTNING: sum.SIDBRYTNING,
                                              BENAMNING_FLERA_RADER:
                                                sum.BENAMNING_FLERA_RADER,
                                              BENAMNING_BLANK:
                                                sum.BENAMNING_BLANK,
                                              DELSUMMA: sum.DELSUMMA,
                                              SKRIV_DELSUMMA:
                                                sum.SKRIV_DELSUMMA,
                                              Typ: sum.Typ,
                                              attributename: sum.attributename,
                                            };
                                          })
                                      : [],
                                  };
                                })
                            : [],
                        };
                      })
                  : [];

              const grundUrval =
                data && data.ek_grundurval
                  ? data.ek_grundurval.reduce<IGrundurval[]>((a, b) => {
                      if (
                        b.NAME &&
                        b.VISIBILITY === STANDARD_GRUNDURVAL_VISIBILITY
                      ) {
                        a.push({ id: b.id, name: b.NAME });
                      }
                      return a;
                    }, [])
                  : undefined;

              const standardColumns =
                data && data.ekanalysstandardcolumns
                  ? (data.ekanalysstandardcolumns.filter(
                      x => x !== null
                    ) as StandardColumnType[])
                  : [];

              const hasWholeCodeBook = data
                ? data.ekuserharhelahuvudboken
                : false;

              return props.children({
                loading: false,
                availableCategories,
                grundUrval,
                standardColumns,
                hasWholeCodeBook,
              });
            }}
          </Query>
        );
      }}
    </KodplanConsumer>
  );
};

export default EkonomiskAnalysBaseParamsQuery;
